import { type Font as OpentypeFont } from 'opentype.js'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { FontFamily } from '@/api/types/font-family'
import { getShapeTextFlowBoundary } from '@/lib/boundaries/text-boundary'
import { shapeTextFlowWithWrap } from '@/lib/shapes/shape-text-wrap-bands'
import { buildShapeTextMeasurer } from '@/lib/shapes/shape-text-measure'
import { layoutShapeTextContent, paragraphsToPlainText } from '@/lib/shapes/shape-text'
import { findFontAndFamilyByFontId } from '@/lib/fonts/font-loader'
import { DEFAULT_FONT_ID } from '@/lib/text/constants/text-defaults'
import { logger } from '@/utils/logger'
import type { TextShapeOutline } from '@/lib/text/types/text-shape-outline'
import { loadOpentypeFont } from './handlers/load-opentype-font'
import { serializeGlyphPath } from './helpers/serialize-glyph-path'
import { buildFontLookupFromFamilies } from './helpers/build-font-lookup-from-families'

/**
 * Extracts the real glyph outlines for a shape's text, positioned exactly where the shape-text
 * engine currently lays them out (same measurer/flow/layout pipeline `useShapeTextLayout` uses for
 * on-screen rendering — see that hook's doc comment), and merges every character's closed contour
 * into one compound SVG path.
 *
 * Client-side, not a server round-trip: fonts are already loaded as real WOFF files for rendering
 * (`font.woffFilePath`, `lib/fonts/font-loader.ts`) — this re-fetches those same files and parses
 * them with `opentype.js` to get each glyph's true vector outline, rather than approximating it
 * from anything measured in the DOM.
 *
 * Known simplification: does not account for text-wrap-around-obstacles exclusion zones (the
 * `wrap` context `useShapeTextLayout` can take) — a frame currently being narrowed by a nearby
 * obstacle will convert using its unobstructed layout instead. Not expected to matter for the
 * short (≤50 character), single-purpose text this feature targets; flagged here for follow-up if
 * it ever does.
 *
 * Returns `null` when there is no text, or no character could be resolved to a loadable font
 * (e.g. every font failed to fetch).
 */
export async function buildTextShapeOutline(
  shape: ShapeData,
  fontFamilies: FontFamily[]
): Promise<TextShapeOutline | null> {
  const paragraphs = shape.paragraphs ?? []

  if (paragraphs.length === 0) return null

  const text = paragraphsToPlainText(paragraphs)

  if (text.length === 0) return null

  const fontLookup = buildFontLookupFromFamilies(fontFamilies)
  const measurer = buildShapeTextMeasurer(paragraphs, fontLookup)
  const { charRunMap, measureTextPt, pointPt, leadingMult, pointSize: framePointSize } = measurer
  const flow = shapeTextFlowWithWrap(shape, getShapeTextFlowBoundary(shape, pointPt, leadingMult))
  const paragraphAlignments = paragraphs.map((p) => p.alignment ?? 0)
  const layout = layoutShapeTextContent(text, flow, measureTextPt, paragraphAlignments)

  const fontIdsUsed = new Set<number>()

  for (let i = 0; i < layout.consumedLength; i++) {
    if (/\s/.test(text[i] ?? '')) continue
    fontIdsUsed.add(charRunMap[i]?.fontId ?? DEFAULT_FONT_ID)
  }

  const fontsById = new Map<number, OpentypeFont>()

  await Promise.all(
    Array.from(fontIdsUsed, async (fontId) => {
      const found = findFontAndFamilyByFontId(fontFamilies, fontId)

      if (!found?.font.woffFilePath) return
      const parsed = await loadOpentypeFont(found.font.woffFilePath)

      if (parsed) fontsById.set(fontId, parsed)
    })
  )

  const baselineByYTop = new Map<number, number>()

  for (const row of flow.rows) baselineByYTop.set(row.yTop, row.baselineY)

  const pathParts: string[] = []
  const skippedCharacters: string[] = []
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (let i = 0; i < layout.consumedLength; i++) {
    const ch = text[i]

    if (!ch || /\s/.test(ch)) continue

    const caret = layout.carets[i]

    if (!caret) continue

    const run = charRunMap[i]
    const fontId = run?.fontId ?? DEFAULT_FONT_ID
    const font = fontsById.get(fontId)

    if (!font) continue

    const fontSizePt = run?.pointSize ?? framePointSize
    // `carets[i]` is "the caret at text offset i", which at a wrap/segment-jump boundary stores the
    // position where the PREVIOUS row/segment ENDED on `x`/`yTop` — `nextSlotX`/`nextSlotYTop` carry
    // where the flow actually CONTINUES, i.e. where character i (the first of the new row/segment)
    // really gets painted. Without this, the first character after a wrap converted at the END of
    // the previous line instead of the start of its own (WMPCM-9637 follow-up). Same convention as
    // `xOf`/`rowTopOf` in `shape-text-navigation.ts`.
    const caretX = caret.nextSlotX ?? caret.x
    const caretYTop = caret.nextSlotYTop ?? caret.yTop
    // Rows and carets are derived from the same `flow.rows` array, so this lookup always hits;
    // the fallback only guards a future change to that invariant.
    const baselineY = baselineByYTop.get(caretYTop) ?? caretYTop + fontSizePt * 0.8
    const glyphPath = font.getPath(ch, caretX, baselineY, fontSizePt)
    // Serialize ourselves instead of `glyphPath.toPathData()` — see `serializeGlyphPath`'s doc
    // comment for the confirmed opentype.js rounding bug this avoids (WMPCM-9637 follow-up).
    const d = serializeGlyphPath(glyphPath)

    if (!d) continue

    if (d.includes('NaN')) {
      // Not expected to trigger — `serializeGlyphPath` doesn't hit the rounding bug that caused
      // this in the wild — but skip (rather than emit a corrupt shape) if some other glyph or
      // font ever produces a genuinely NaN coordinate for an unrelated reason.
      const glyphIndex = font.charToGlyphIndex(ch)
      const glyph = font.glyphs.get(glyphIndex)

      logger.error('Text-to-shape: glyph produced a NaN outline, skipping character', {
        shapeId: shape.id,
        charIndex: i,
        char: ch,
        caretX,
        caretYTop,
        baselineY,
        fontSizePt,
        fontId,
        unitsPerEm: font.unitsPerEm,
        glyphIndex,
        rawXMin: glyph.xMin,
        rawXMax: glyph.xMax,
        rawYMin: glyph.yMin,
        rawYMax: glyph.yMax,
        rawNumberOfContours: glyph.numberOfContours,
        rawPathCommands: glyph.path?.commands,
      })
      skippedCharacters.push(ch)
      continue
    }

    pathParts.push(d)

    const bbox = glyphPath.getBoundingBox()

    if (bbox.x1 < minX) minX = bbox.x1
    if (bbox.y1 < minY) minY = bbox.y1
    if (bbox.x2 > maxX) maxX = bbox.x2
    if (bbox.y2 > maxY) maxY = bbox.y2
  }

  if (pathParts.length === 0 || !Number.isFinite(minX)) return null

  return {
    path: pathParts.join(' '),
    x: minX,
    y: minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
    skippedCharacters,
  }
}
